/**
 * Dashboard Page
 * ==============
 * Overview of inventory with quick stats.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Package, FolderTree, MapPin, Search, Camera } from 'lucide-react'
import { Card, CardContent } from '@/components/Card'
import BarcodeScannerModal from '@/components/BarcodeScannerModal'
import { useProducts } from '@/hooks/useProducts'
import { useCategories } from '@/hooks/useCategories'
import { useLocations } from '@/hooks/useLocations'
import { productApi } from '@/services/api'
import { getProductThumbnailUrl } from '@/utils/images'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  loading?: boolean
  to?: string
}

function StatCard({ title, value, icon, loading, to }: StatCardProps) {
  const content = (
    <CardContent className="flex items-center gap-4">
      <div className="p-3 rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400">
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {loading ? '...' : value}
        </p>
      </div>
    </CardContent>
  )

  if (to) {
    return (
      <Link to={to} className="block">
        <Card className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
          {content}
        </Card>
      </Link>
    )
  }

  return <Card>{content}</Card>
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false)
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: productsData, isLoading: productsLoading } = useProducts({ limit: 1 })
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { data: locationsData, isLoading: locationsLoading } = useLocations()

  // Search for autocomplete results (only when 2+ characters)
  const searchTerm = searchQuery.trim()
  const { data: searchResults, isLoading: searchLoading } = useProducts(
    {
      search: searchTerm,
      limit: 8,
    },
    { enabled: searchTerm.length >= 2 }
  )

  const totalProducts = productsData?.meta?.total ?? 0
  const totalCategories = categoriesData?.data?.length ?? 0
  const totalLocations = locationsData?.data?.length ?? 0

  const suggestions = searchTerm.length >= 2 ? (searchResults?.data ?? []) : []

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setShowDropdown(false)
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setShowDropdown(true)
    setSelectedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      const product = suggestions[selectedIndex]
      setShowDropdown(false)
      navigate(`/products/${product.product_id}`)
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleSuggestionClick = (productId: string) => {
    setShowDropdown(false)
    navigate(`/products/${productId}`)
  }

  // Handle barcode scanned from camera
  const handleBarcodeScanned = async (barcode: string) => {
    setShowBarcodeScanner(false)
    setScannedBarcode(barcode)

    try {
      // Search for product by barcode
      const response = await productApi.list({ search: barcode, limit: 1 })
      const products = response.data

      // Check if we found a product with this exact barcode
      const matchingProduct = products?.find(
        (p) => p.barcode === barcode
      )

      if (matchingProduct) {
        // Found - navigate to product view
        navigate(`/products/${matchingProduct.product_id}`)
      } else {
        // Not found - show dialog asking if user wants to add it
        setShowNotFoundDialog(true)
      }
    } catch (error) {
      console.error('Error searching for barcode:', error)
      // On error, offer to add the product
      setShowNotFoundDialog(true)
    }
  }

  // Handle user choosing to add the scanned barcode as a new product
  const handleAddNewProduct = () => {
    setShowNotFoundDialog(false)
    if (scannedBarcode) {
      navigate(`/products/new?barcode=${encodeURIComponent(scannedBarcode)}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400">Welcome to Exodus Asset Management</p>
      </div>

      {/* Stats grid - now 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Products"
          value={totalProducts}
          icon={<Package size={24} />}
          loading={productsLoading}
          to="/products"
        />
        <StatCard
          title="Categories"
          value={totalCategories}
          icon={<FolderTree size={24} />}
          loading={categoriesLoading}
          to="/categories"
        />
        <StatCard
          title="Locations"
          value={totalLocations}
          icon={<MapPin size={24} />}
          loading={locationsLoading}
          to="/locations"
        />
      </div>

      {/* Search box - full width in the middle */}
      <Card>
        <CardContent>
          <div ref={searchRef} className="relative">
            <form onSubmit={handleSearch}>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search by product ID, SKU, name, barcode, service tag, manufacturer, or details..."
                    className="w-full pl-12 pr-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                  {searchLoading && searchTerm.length >= 2 && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowBarcodeScanner(true)}
                  className="flex-shrink-0 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title="Scan barcode with camera"
                >
                  <Camera size={20} />
                </button>
              </div>
            </form>

            {/* Autocomplete dropdown */}
            {showDropdown && searchTerm.length >= 2 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {suggestions.length === 0 && !searchLoading ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No products found for "{searchQuery}"
                  </div>
                ) : (
                  <>
                    {suggestions.map((product, index) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleSuggestionClick(product.product_id)}
                        className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          index === selectedIndex ? 'bg-primary-50 dark:bg-primary-900/50' : ''
                        } ${index !== suggestions.length - 1 ? 'border-b dark:border-gray-700' : ''}`}
                      >
                        {getProductThumbnailUrl(product) ? (
                          <img
                            src={getProductThumbnailUrl(product)!}
                            alt={product.name}
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <Package className="text-gray-400" size={20} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {product.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {product.product_id}
                            {product.sku && ` • ${product.sku}`}
                            {product.barcode && ` • ${product.barcode}`}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                            product.quantity === 0
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                              : product.quantity < 5
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                          }`}
                        >
                          {product.quantity}
                        </span>
                      </button>
                    ))}
                    {suggestions.length > 0 && (
                      <button
                        type="button"
                        onClick={handleSearch}
                        className="w-full px-4 py-3 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium border-t dark:border-gray-700"
                      >
                        View all results for "{searchQuery}"
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick actions */}
      <Card>
        <CardContent>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/products/new"
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Package className="text-primary-600 dark:text-primary-400" size={20} />
              <span className="font-medium dark:text-white">Add Product</span>
            </Link>
            <Link
              to="/categories"
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FolderTree className="text-primary-600 dark:text-primary-400" size={20} />
              <span className="font-medium dark:text-white">Manage Categories</span>
            </Link>
            <Link
              to="/locations"
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <MapPin className="text-primary-600 dark:text-primary-400" size={20} />
              <span className="font-medium dark:text-white">Manage Locations</span>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <BarcodeScannerModal
          onClose={() => setShowBarcodeScanner(false)}
          onScan={handleBarcodeScanned}
        />
      )}

      {/* Product Not Found Dialog */}
      {showNotFoundDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowNotFoundDialog(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Product Not Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No product found with barcode <span className="font-mono font-medium">{scannedBarcode}</span>. Would you like to add it as a new product?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotFoundDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNewProduct}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
